import confetti from 'canvas-confetti'

type ConfettiStartOptions = {
	particles?: Array<{ type?: string }>
	size?: number
	dropRate?: number
	defaultColors?: string[]
	canvasElement?: HTMLCanvasElement | null
}

type ConfettiController = {
	start: (options?: ConfettiStartOptions) => void
	stop: () => void
}

export default defineNuxtPlugin(() => {
	let activeCanvas: HTMLCanvasElement | null = null
	let activeInstance: ReturnType<typeof confetti.create> | null = null
	let frameId: number | null = null

	const stop = () => {
		if (frameId !== null) {
			window.cancelAnimationFrame(frameId)
			frameId = null
		}

		activeInstance?.reset()
		activeInstance = null
		activeCanvas = null
	}

	const start = (options: ConfettiStartOptions = {}) => {
		const { size = 5, dropRate = 40, defaultColors = ['#04AD33'], canvasElement = null } = options

		if (!(canvasElement instanceof HTMLCanvasElement)) return

		const rect = canvasElement.getBoundingClientRect()
		if (rect.width === 0 || rect.height === 0) return

		canvasElement.width = Math.round(rect.width * window.devicePixelRatio)
		canvasElement.height = Math.round(rect.height * window.devicePixelRatio)
		canvasElement.style.pointerEvents = 'none'

		if (activeCanvas !== canvasElement) {
			stop()
			activeCanvas = canvasElement
			activeInstance = confetti.create(canvasElement, {
				resize: true,
				useWorker: false,
			})
		} else if (!activeInstance) {
			activeInstance = confetti.create(canvasElement, {
				resize: true,
				useWorker: false,
			})
		}

		if (frameId !== null) {
			window.cancelAnimationFrame(frameId)
		}

		const particleCount = Math.max(Math.round(dropRate / 5), 6)
		const scalar = Math.max(size / 5, 0.6)
		const intervalMs = 180
		let lastTick = 0

		const fire = () => {
			activeInstance?.({
				particleCount,
				angle: 90,
				spread: 55,
				startVelocity: 22,
				gravity: 0.9,
				decay: 0.92,
				scalar,
				colors: defaultColors,
				shapes: ['square'],
				origin: {
					x: Math.random() * 0.8 + 0.1,
					y: 0,
				},
			})
		}

		fire()

		const loop = (timestamp: number) => {
			if (timestamp - lastTick >= intervalMs) {
				fire()
				lastTick = timestamp
			}

			frameId = window.requestAnimationFrame(loop)
		}

		frameId = window.requestAnimationFrame(loop)
	}

	const controller: ConfettiController = {
		start,
		stop,
	}

	return {
		provide: {
			confetti: controller,
		},
	}
})
